import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import logoUrl from '../../assets/images/Senerfi-formato.png';
import { getQualityPrintMeta } from './qualityPrintConfig';

// PDF institucional FT-GTH-022 "Solicitud novedades personal" (versión 1).
// Reproduce la estructura del formato en Excel: encabezado documental,
// identificación, casillas de tipo de solicitud, fechas/horas, justificación
// y las 4 firmas (trabajador, jefe inmediato, director/coordinador y
// recibido Talento Humano). Los datos vienen SOLO de novelty_pdf_data, que
// el servidor entrega únicamente en estado Recibido; las firmas son las de
// las acciones realmente registradas (si una no existe, el recuadro queda
// vacío con su leyenda). Mismo mecanismo que los demás PDF de NOVA
// (iframe -> html2canvas -> jsPDF).

const FT_BOXES = [
  ['maternidad', 'Licencia Maternidad/ Paternidad'],
  ['matrimonio', 'Licencia de Matrimonio'],
  ['no_remunerada', 'Licencia no remunerada'],
  ['luto', 'Licencia por luto'],
  ['permiso', 'Permiso'],
  ['calamidad', 'Calamidad'],
  ['compensatorio', 'Compensatorio'],
  ['casa', 'Trabajo en casa'],
  ['campo', 'Salida actividades de campo'],
];

function esc(v) {
  return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtDate(value) {
  const s = String(value ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}

function daysInclusive(a, b) {
  const s = String(a || '').slice(0, 10);
  const e = String(b || a || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || !/^\d{4}-\d{2}-\d{2}$/.test(e)) return '';
  const d = Math.round((new Date(`${e}T00:00:00`) - new Date(`${s}T00:00:00`)) / 86400000) + 1;
  return d > 0 ? String(d) : '';
}

async function toDataUrl(src) {
  const u = String(src || '').trim();
  if (!u) return '';
  if (/^data:image\//i.test(u)) return u;
  try {
    const resp = await fetch(u, { cache: 'no-store' });
    if (!resp.ok) return '';
    const blob = await resp.blob();
    if (blob.type && !String(blob.type).toLowerCase().startsWith('image/')) return '';
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return '';
  }
}

async function buildFrame(n) {
  const q = await getQualityPrintMeta('hr_novelty');
  const basePath = (process.env.PUBLIC_URL || '/').replace(/\/+$/g, '');
  let logo = '';
  for (const c of [`${basePath}${logoUrl}`, `${basePath}/Senerfi-formato.png`]) {
    logo = await toDataUrl(c);
    if (logo) break;
  }
  const [sCollab, sBoss, sDir, sHr] = await Promise.all([
    toDataUrl(n.collaborator_signature_url), toDataUrl(n.boss_signature_url),
    toDataUrl(n.director_signature_url), toDataUrl(n.hr_signature_url),
  ]);
  const timeBased = !!(n.start_time && n.end_time);
  const hours = Number(n.hours_decimal || 0);
  const box = (key, label) => `<td class="lbl">${esc(label)}</td><td class="chk">${n.ft_box === key ? 'X' : ''}</td>`;
  const sig = (img, title, name, at, empty) => `
    <td class="sig">
      <div class="sig-img">${img ? `<img src="${img}"/>` : `<span class="muted">${esc(empty)}</span>`}</div>
      <div class="sig-name">${esc(name || '')}${at ? `<br/><span class="muted">${esc(fmtDate(at))} ${esc(String(at).slice(11, 16))}</span>` : ''}</div>
      <div class="sig-title">${esc(title)}</div>
    </td>`;
  const isFt = !!n.ft_box;

  const frame = document.createElement('iframe');
  Object.assign(frame.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(frame);
  const w = frame.contentWindow;
  const doc = w.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"/><style>
    * { box-sizing: border-box; font-family: Arial, Helvetica, sans-serif; }
    body { margin: 0; background: #fff; color: #000; }
    .sheet { width: 980px; padding: 24px; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    td { border: 1px solid #000; padding: 5px 7px; font-size: 12px; vertical-align: middle; word-wrap: break-word; }
    .head td { text-align: center; font-weight: bold; }
    .head .logo img { max-width: 150px; max-height: 64px; }
    .head .meta { text-align: left; font-weight: normal; font-size: 11px; }
    .k { font-weight: bold; background: #f2f2f2; }
    .sec { font-weight: bold; text-align: center; background: #e7e6e6; }
    .lbl { font-size: 11px; }
    .chk { width: 34px; text-align: center; font-weight: bold; font-size: 15px; }
    .just { height: 92px; vertical-align: top; white-space: pre-wrap; }
    .sig { height: 150px; text-align: center; vertical-align: bottom; padding: 4px; }
    .sig-img { height: 82px; display: flex; align-items: center; justify-content: center; }
    .sig-img img { max-height: 80px; max-width: 95%; }
    .sig-name { font-size: 11px; font-weight: bold; min-height: 28px; border-top: 1px solid #000; padding-top: 3px; }
    .sig-title { font-size: 11px; }
    .muted { color: #666; font-weight: normal; font-size: 10px; }
    .note { font-size: 11px; font-weight: bold; }
    .other { font-size: 11px; margin-top: 4px; }
    .foot { margin-top: 8px; font-size: 9px; color: #555; text-align: right; }
  </style></head><body><div class="sheet">
    <table class="head">
      <colgroup><col style="width:20%"/><col style="width:58%"/><col style="width:22%"/></colgroup>
      <tr><td rowspan="3" class="logo">${logo ? `<img src="${logo}"/>` : 'SENERFI'}</td><td>SOLUCIONES ENERGETICAS FORESTALES INTEGRALES SAS</td><td class="meta">CÓDIGO: ${esc(q.code || 'FT-GTH-022')}</td></tr>
      <tr><td>GESTIÓN DE TALENTO HUMANO</td><td class="meta">VERSIÓN: ${esc(q.version || '1')}</td></tr>
      <tr><td>SOLICITUD NOVEDADES PERSONAL</td><td class="meta">FECHA: ${esc(q.effective_date ? fmtDate(q.effective_date) : '09/12/2025')}</td></tr>
    </table>
    <table style="margin-top:6px">
      <colgroup><col style="width:12%"/><col style="width:40%"/><col style="width:14%"/><col style="width:16%"/><col style="width:6%"/><col style="width:12%"/></colgroup>
      <tr><td class="k">Nombre</td><td>${esc(n.collaborator_name)}</td><td class="k">Cédula</td><td>${esc(n.collaborator_dni)}</td><td class="k">No.</td><td>${esc(n.radicado || n.id)}</td></tr>
      <tr><td class="k">Cargo</td><td>${esc(n.collaborator_cargo || '')}</td><td class="k">Área/Proyecto</td><td colspan="3">${esc(n.collaborator_area || '')}</td></tr>
    </table>
    <table style="margin-top:6px">
      <tr><td class="sec" colspan="8">TIPO DE SOLICITUD</td></tr>
      <tr>${FT_BOXES.slice(0, 4).map(([k, l]) => box(k, l)).join('')}</tr>
      <tr>${FT_BOXES.slice(4, 8).map(([k, l]) => box(k, l)).join('')}</tr>
      <tr>${box(FT_BOXES[8][0], FT_BOXES[8][1])}<td colspan="6" class="lbl">${isFt ? '' : `Otro: <b>${esc(n.novelty_type)}</b>`}</td></tr>
    </table>
    <table style="margin-top:6px">
      <colgroup><col style="width:16%"/><col style="width:18%"/><col style="width:14%"/><col style="width:18%"/><col style="width:14%"/><col style="width:20%"/></colgroup>
      <tr><td class="k">Fecha de Solicitud</td><td colspan="5">${esc(fmtDate(n.submitted_at || n.created_at))}${Number(n.extemporanea) === 1 ? ' &nbsp;<b>(Extemporánea)</b>' : ''}</td></tr>
      <tr><td class="k">Fecha de Inicio</td><td>${esc(fmtDate(n.novelty_date))}</td><td class="k">Fecha final</td><td>${esc(fmtDate(n.novelty_date_end || n.novelty_date))}</td><td class="k">No. Días</td><td>${timeBased ? '' : esc(daysInclusive(n.novelty_date, n.novelty_date_end))}</td></tr>
      <tr><td class="k">Hora de inicio</td><td>${esc(String(n.start_time || '').slice(0, 5))}</td><td class="k">Hora final</td><td>${esc(String(n.end_time || '').slice(0, 5))}</td><td class="k">No. Horas</td><td>${timeBased && hours > 0 ? esc(String(hours).replace('.', ',')) : ''}</td></tr>
    </table>
    <table style="margin-top:6px">
      <tr><td class="k">Justificación de la novedad:</td></tr>
      <tr><td class="just">${esc(n.reason || '')}${Number(n.extemporanea) === 1 && n.extemporanea_motivo ? `\n\nMotivo de la solicitud extemporánea: ${esc(n.extemporanea_motivo)}` : ''}</td></tr>
    </table>
    <table style="margin-top:6px">
      <tr>
        ${sig(sCollab, 'Firma del Trabajador', n.collaborator_name, n.submitted_at, 'Sin firma')}
        ${sig(sBoss, 'Autorización jefe inmediato', n.boss_approved_by_name, n.boss_approved_at, 'Sin autorización')}
        ${sig(sDir, 'Autoriza Director/Coordinador', n.director_by_name, n.director_at, Number(n.director_required) === 1 ? 'Sin autorización' : 'No requerida')}
        ${sig(sHr, 'Recibido Talento Humano', n.hr_received_by_name, n.hr_received_at, 'Sin recibido')}
      </tr>
    </table>
    <div class="note" style="margin-top:6px">NOTA: Las solicitudes de permiso laboral se deben presentar ocho (8) días hábiles antes de la fecha, a excepción de calamidades.</div>
    <div class="foot">Generado por NOVA · versión del documento ${esc(n.pdf_version || 1)} · ${esc(new Date().toLocaleString('es-CO'))}</div>
  </div><script>window.onload = () => { window.__NOV_READY__ = true; };</script></body></html>`);
  doc.close();
  return { frame, w };
}

export async function generateNoveltyPdfBlob(novelty) {
  const { frame, w } = await buildFrame(novelty || {});
  try {
    for (let i = 0; i < 60 && !w.__NOV_READY__; i++) await new Promise((r) => setTimeout(r, 50));
    const imgs = Array.from(w.document.images || []);
    await Promise.all(imgs.map((img) => (img.complete ? null : new Promise((res) => { img.onload = res; img.onerror = res; }))));
    const el = w.document.querySelector('.sheet') || w.document.body;
    const scale = 2;
    const canvas = await html2canvas(el, { scale, backgroundColor: '#FFFFFF', useCORS: true, windowWidth: 1030 });
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'letter' });
    const margin = 8;
    const pageW = pdf.internal.pageSize.getWidth() - margin * 2;
    const pageH = pdf.internal.pageSize.getHeight() - margin * 2;
    const ratio = Math.min(pageW / canvas.width, pageH / canvas.height);
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, canvas.width * ratio, canvas.height * ratio, undefined, 'FAST');
    return pdf.output('blob');
  } finally {
    try { frame.remove(); } catch (_) {}
  }
}
